import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import { Plus, Pencil, Trash2, AlertCircle, Lock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { PageLoader } from '@/components/ui/Spinner';
import { Modal } from '@/components/ui/Modal';
import { CategoryForm } from '@/components/categories/CategoryForm';
import {
  useCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
} from '@/hooks/useCategories';
import { useTranslation } from '@/i18n';
import type { Category, CreateCategoryInput } from '@/types/category';

export const CategoriesPage: React.FC = () => {
  const { t, locale } = useTranslation();
  const [isFormOpen, setIsFormOpen] = useState(false);

  // Get localized category name based on current locale
  const getCategoryName = (category: Category): string => {
    // For Portuguese, use the original name; for other locales, prefer nameEn
    if (locale === 'pt-BR') {
      return category.name;
    }
    return category.nameEn || category.name;
  };
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null);

  const { data, isLoading, isError } = useCategories();
  const createMutation = useCreateCategory();
  const updateMutation = useUpdateCategory();
  const deleteMutation = useDeleteCategory();

  const categories = data?.categories || [];
  const systemCategories = categories.filter((c) => c.isSystem);
  const customCategories = categories.filter((c) => !c.isSystem);

  const handleCreate = () => {
    setEditingCategory(null);
    setIsFormOpen(true);
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (formData: CreateCategoryInput) => {
    if (editingCategory) {
      await updateMutation.mutateAsync({
        id: editingCategory.id,
        data: formData,
      });
    } else {
      await createMutation.mutateAsync(formData);
    }
    setIsFormOpen(false);
    setEditingCategory(null);
  };

  const handleDelete = async () => {
    if (deletingCategory) {
      await deleteMutation.mutateAsync(deletingCategory.id);
      setDeletingCategory(null);
    }
  };

  const renderIcon = (iconName: string, className: string = 'w-5 h-5') => {
    const IconComponent = (Icons as unknown as Record<string, React.ElementType>)[iconName];
    return IconComponent ? <IconComponent className={className} /> : null;
  };

  if (isLoading) {
    return <PageLoader message={t.app.categories.loading} />;
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t.app.categories.error}
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          {t.app.categories.errorDesc}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t.app.categories.title}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t.app.categories.subtitle}
          </p>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={handleCreate}>
          {t.app.categories.newCategory}
        </Button>
      </div>

      {/* Custom Categories */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t.app.categories.myCategories}
        </h2>
        {customCategories.length === 0 ? (
          <Card className="p-8 text-center">
            <Icons.Tag className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {t.app.categories.noCustomCategories}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto mb-4">
              {t.app.categories.noCustomCategoriesDesc}
            </p>
            <Button onClick={handleCreate} leftIcon={<Plus className="w-4 h-4" />}>
              {t.app.categories.createFirst}
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {customCategories.map((category) => (
              <Card key={category.id} className="group hover:shadow-md transition-shadow">
                <CardContent className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{
                        backgroundColor: `${category.color}20`,
                        color: category.color,
                      }}
                    >
                      {renderIcon(category.icon)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {getCategoryName(category)}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge
                          variant={category.type === 'income' ? 'success' : 'danger'}
                          size="sm"
                        >
                          {category.type === 'income' ? t.app.categories.type.income : t.app.categories.type.expense}
                        </Badge>
                        {(category.deductibleBR || category.deductibleUK) && (
                          <Badge variant="info" size="sm">
                            {t.app.categories.deductible}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(category)}
                      className="p-2"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeletingCategory(category)}
                      className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* System Categories */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t.app.categories.systemCategories}
          </h2>
          <Lock className="w-4 h-4 text-gray-400" />
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {t.app.categories.systemCategoriesDesc}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {systemCategories.map((category) => (
            <div
              key={category.id}
              className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-secondary-800 rounded-lg"
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{
                  backgroundColor: `${category.color}20`,
                  color: category.color,
                }}
              >
                {renderIcon(category.icon, 'w-4 h-4')}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                  {getCategoryName(category)}
                </p>
              </div>
              <Badge
                variant={category.type === 'income' ? 'success' : 'secondary'}
                size="sm"
              >
                {category.type === 'income' ? 'R' : 'D'}
              </Badge>
            </div>
          ))}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <CategoryForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingCategory(null);
        }}
        onSubmit={handleFormSubmit}
        category={editingCategory}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingCategory}
        onClose={() => setDeletingCategory(null)}
        title={t.app.categories.confirmDelete}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            {t.app.categories.confirmDeleteMsg}{' '}
            <span className="font-semibold text-gray-900 dark:text-white">
              {deletingCategory?.name}
            </span>
            ?
          </p>
          <p className="text-sm text-yellow-600 dark:text-yellow-500">
            {t.app.categories.deleteWarning}
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={() => setDeletingCategory(null)}>
              {t.app.common.cancel}
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              isLoading={deleteMutation.isPending}
            >
              {t.app.common.delete}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CategoriesPage;
